import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Search, UserCog,
} from "lucide-react";
import { SignatureOnFileDialog } from "@/components/SignatureOnFileDialog";
import { BulkReassignDialog } from "@/components/BulkReassignDialog";
import { DealerTable } from "@/components/ExistingDealerTable";

const CATEGORIES = [
  ["product_configuration", "Product Configuration"],
  ["pricing", "Pricing"],
  ["forms", "Forms"],
  ["user_access", "User Access"],
  ["dealer_information", "Dealer Information"],
  ["eligibility", "Eligibility"],
  ["contract_generation", "Contract Generation"],
  ["reporting", "Reporting"],
  ["ach", "ACH"],
  ["documents", "Documents"],
  ["other", "Other"],
];
const PRIORITIES = [["normal", "Normal"], ["high", "High"], ["critical", "Critical"]];

const SLA_PILL = {
  ok:       { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "On track" },
  warn:     { cls: "bg-amber-50 text-amber-700 border-amber-300",       label: "Watch" },
  overdue:  { cls: "bg-red-50 text-red-700 border-red-300",             label: "Overdue" },
  terminal: { cls: "bg-slate-50 text-slate-600 border-slate-200",       label: "Closed" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function ExistingDealers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [sof, setSof] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [agencyF, setAgencyF] = useState("");
  const [agentF, setAgentF] = useState("");
  const agencies = useMemo(() => [...new Set(rows.map((r) => r.agency).filter(Boolean))].sort(), [rows]);
  const agents = useMemo(() => [...new Set(rows.filter((r) => !agencyF || r.agency === agencyF).map((r) => r.signing_agent_name).filter(Boolean))].sort(), [rows, agencyF]);
  const openPacket = async (r) => {
    try {
      const { data } = await api.get(`/dealers/${r.dealer_id}/packet.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Could not open agreement packet"); }
  };
  const [crDlg, setCrDlg] = useState(null);           // {dealer, title, desc, category, priority, saving}
  const [historyFor, setHistoryFor] = useState(null); // dealer row
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/existing-dealers");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dealers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      (!agencyF || r.agency === agencyF) && (!agentF || r.signing_agent_name === agentF) &&
      (!needle || (r.dealer_name || "").toLowerCase().includes(needle) || (r.dba || "").toLowerCase().includes(needle) ||
        (r.agency || "").toLowerCase().includes(needle) || (r.signing_agent_name || "").toLowerCase().includes(needle))
    ).sort((a, b) => (a.dealer_name || "").localeCompare(b.dealer_name || "", undefined, { sensitivity: "base" }));
  }, [rows, q, agencyF, agentF]);

  const openRequest = (dealer) => {
    setCrDlg({ dealer, title: "", desc: "", category: "product_configuration", priority: "normal", saving: false });
  };

  const submitRequest = async () => {
    if (!crDlg?.title?.trim()) { toast.error("Title required"); return; }
    setCrDlg((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.post("/change-requests", {
        source_onboarding_id: crDlg.dealer.onboarding_id,
        title: crDlg.title.trim(),
        description: crDlg.desc || "",
        category: crDlg.category,
        priority: crDlg.priority,
      });
      toast.success("Change request submitted · 24-hour SLA");
      setCrDlg(null);
      load();
      // Optional: navigate straight to the record
      window.location.href = `/onboarding/${data.onboarding_id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
      setCrDlg((s) => ({ ...s, saving: false }));
    }
  };

  const openHistory = async (dealer) => {
    setHistoryFor(dealer);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/change-requests/history/${dealer.onboarding_id}`);
      setHistory(data || []);
    } catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <SignatureOnFileDialog dealer={sof} open={!!sof} onClose={() => setSof(null)} onDone={load} />
      {isAdmin && <BulkReassignDialog dealers={rows.filter((r) => selected.includes(r.dealer_id))} open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={() => { setSelected([]); load(); }} />}
      {isAdmin && selected.length > 0 && (
        <div className="sticky top-16 z-30 mx-auto max-w-[1400px] px-4 sm:px-6 pt-3" data-testid="bulk-bar">
          <div className="bg-[#0B132B] text-white rounded-sm shadow-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">{selected.length} dealer{selected.length === 1 ? "" : "s"} selected</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-8 rounded-sm bg-transparent border-white/40 text-white hover:bg-white/10" onClick={() => setSelected(filtered.map((r) => r.dealer_id))} data-testid="bulk-select-all">Select all shown</Button>
              <Button size="sm" variant="outline" className="h-8 rounded-sm bg-transparent border-white/40 text-white hover:bg-white/10" onClick={() => setSelected([])} data-testid="bulk-clear">Clear</Button>
              <Button size="sm" className="h-8 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold" onClick={() => setBulkOpen(true)} data-testid="bulk-reassign-btn"><UserCog size={13} className="mr-1" /> Reassign</Button>
            </div>
          </div>
        </div>)}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="existing-dealers-page">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Dealer Signup</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] leading-none mt-1">Existing Dealers</h1>
            <p className="text-sm text-[#6C757D] mt-2 max-w-xl">
              Every dealer with a signed agreement, A–Z. Icons across the top show what's still missing on each line; hover an icon for details.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            <select value={agencyF} onChange={(e) => { setAgencyF(e.target.value); setAgentF(""); }} className="h-10 rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="existing-dealers-agency-filter">
              <option value="">All agencies</option>{agencies.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={agentF} onChange={(e) => setAgentF(e.target.value)} className="h-10 rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="existing-dealers-agent-filter">
              <option value="">All agents</option>{agents.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          <div className="relative w-full sm:w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
            <Input
              placeholder="Search dealer, agency, or agent…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-10 rounded-sm bg-white"
              data-testid="existing-dealers-search"
            />
          </div>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[#6C757D] py-16">Loading dealers…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-14 text-center rounded-sm" data-testid="existing-dealers-empty">
            <CheckCircle2 className="mx-auto text-[#6C757D]" size={28} />
            <h3 className="font-display text-xl font-bold text-[#0B132B] mt-3">
              {q ? "No dealers match that search" : "No signed dealers yet"}
            </h3>
            <p className="text-sm text-[#6C757D] mt-2">
              {q ? "Try widening the search." : "Once dealers complete onboarding they appear here so you can request updates directly."}
            </p>
          </Card>
        ) : (
          <DealerTable rows={filtered} isAdmin={isAdmin} selected={selected} toggleSel={toggleSel} onPacket={openPacket} onSof={setSof} onRequest={openRequest} onHistory={openHistory} onChanged={load} />
        )}
      </main>

      {/* Request Change dialog */}
      <Dialog open={!!crDlg} onOpenChange={(o) => { if (!o) setCrDlg(null); }}>
        <DialogContent data-testid="request-change-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request a change · {crDlg?.dealer?.dealer_name}</DialogTitle>
            <DialogDescription className="text-xs text-[#6C757D]">
              Opens a 24-hour SLA ticket in the Updates pipeline. Overdue after 24 hours from creation.
            </DialogDescription>
          </DialogHeader>
          {crDlg && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Title</Label>
                <Input value={crDlg.title}
                       onChange={(e) => setCrDlg({ ...crDlg, title: e.target.value })}
                       placeholder="Update dealer email footer"
                       className="mt-2 h-10 rounded-sm" data-testid="cr-title" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Description</Label>
                <Textarea rows={4} value={crDlg.desc}
                          onChange={(e) => setCrDlg({ ...crDlg, desc: e.target.value })}
                          placeholder="What needs to change and why?"
                          className="mt-2 rounded-sm" data-testid="cr-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Category</Label>
                  <Select value={crDlg.category} onValueChange={(v) => setCrDlg({ ...crDlg, category: v })}>
                    <SelectTrigger className="mt-2 h-10 rounded-sm bg-white text-sm" data-testid="cr-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Priority</Label>
                  <Select value={crDlg.priority} onValueChange={(v) => setCrDlg({ ...crDlg, priority: v })}>
                    <SelectTrigger className="mt-2 h-10 rounded-sm bg-white text-sm" data-testid="cr-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-[11px] text-[#6C757D] italic bg-[#FFFDF5] border border-[#D4AF37]/40 rounded-sm p-2 flex items-start gap-2">
                <AlertTriangle size={12} className="mt-0.5 shrink-0 text-[#8A6D1F]"/>
                Change requests skip signup + program setup and start directly in Production. Total SLA is 24 hours end-to-end.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrDlg(null)} disabled={crDlg?.saving}>Cancel</Button>
            <Button onClick={submitRequest} disabled={crDlg?.saving || !crDlg?.title?.trim()}
                    className="bg-[#0B132B] hover:bg-[#1C2541] text-white"
                    data-testid="btn-submit-change-request">
              {crDlg?.saving ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History side panel */}
      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) { setHistoryFor(null); setHistory([]); } }}>
        <DialogContent data-testid="change-history-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change history · {historyFor?.dealer_name}</DialogTitle>
            <DialogDescription className="text-xs text-[#6C757D]">
              Every change request ever filed against this dealer.
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="text-sm text-[#6C757D] py-6">Loading history…</div>
          ) : history.length === 0 ? (
            <div className="text-sm italic text-[#6C757D] py-6">No change requests filed yet.</div>
          ) : (
            <div className="space-y-2">
              {history.map((h) => {
                const sla = SLA_PILL[h.sla_status] || SLA_PILL.ok;
                return (
                  <Link key={h.id} to={`/onboarding/${h.id}`}
                        className="block rounded-sm border border-[#E9ECEF] bg-white p-3 hover:border-[#D4AF37]/60 transition-colors"
                        data-testid={`change-history-row-${h.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[#0B132B] text-sm">{h.title || "Untitled"}</div>
                        {h.description && <div className="text-xs text-[#6C757D] mt-1 line-clamp-2">{h.description}</div>}
                        <div className="text-[10px] text-[#6C757D] uppercase tracking-[0.14em] mt-2">
                          {(h.category || "other").replace("_", " ")} · {h.priority} priority · {h.created_by_name || "—"}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-bold ${sla.cls}`}>{sla.label}</span>
                        <div className="text-[10px] text-[#6C757D] mt-1">{h.status_label}</div>
                        <div className="text-[10px] text-[#6C757D]">{fmt(h.created_at)}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
